import React, { useState, useEffect } from 'react';
import { createApiUrl } from '../../config/api';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Alert,
  Typography,
  Autocomplete,
  FormHelperText,
  CircularProgress
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';

const TicketForm = ({ ticket, onSubmit, onCancel }) => {
  // TKT-185: distinguish internal/staff tickets (equipment breakdowns,
  // internal complaints) from client-facing tickets. Internal tickets
  // hide the customer section and restrict the category dropdown to the
  // categories that make sense for an internal report.
  const INTERNAL_CATEGORY_KEYWORDS = ['equipment issue', 'complaint'];
  // TKT-186: tickets stay open until staff close them. We no longer
  // surface a duration/expiry field, but we still pass a non-zero
  // sentinel to the backend so the existing INSERT column receives a
  // value. The backend never auto-closes based on this date.
  const [formData, setFormData] = useState({
    customer_id: '',
    category_id: '',
    name: '',
    notes: '',
    task_type_id: '',
    priority_id: '',
    assigned_to_user_id: '',
    is_internal: false,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Metadata
  const [customers, setCustomers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [users, setUsers] = useState([]);

  // Initialize form data if editing
  useEffect(() => {
    if (ticket) {
      setFormData({
        customer_id: ticket.customer_id || '',
        category_id: ticket.category_id || '',
        name: ticket.name || '',
        notes: ticket.notes || '',
        task_type_id: ticket.task_type_id || '',
        priority_id: ticket.priority_id || '',
        assigned_to_user_id: ticket.assigned_to_user_id || '',
        is_internal: !!ticket.is_internal,
      });
    }
  }, [ticket]);

  // Fetch metadata
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        const [metadataRes, usersRes] = await Promise.all([
          fetch(createApiUrl('api/tickets/metadata'), {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(createApiUrl('api/users'), {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }).catch(() => ({ ok: false, json: () => ({ users: [] }) })) // Handle auth error gracefully
        ]);

        const metadataData = await metadataRes.json();
        const usersData = await usersRes.json();

        if (metadataRes.ok) {
          setCategories(metadataData.categories || []);
          setTaskTypes(metadataData.taskTypes || []);
          setPriorities(metadataData.priorities || []);
          setCustomers(metadataData.customers || []);
        }
        if (usersRes.ok) setUsers(usersData.users || []);
      } catch (error) {
        console.error('Failed to fetch metadata:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMetadata();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // TKT-185: customer is only required for client-facing tickets.
    if (!formData.is_internal && !formData.customer_id) {
      newErrors.customer_id = 'Customer is required';
    }
    if (!formData.category_id) {
      newErrors.category_id = 'Category is required';
    }
    if (!formData.name?.trim()) {
      newErrors.name = 'Ticket name is required';
    }
    if (!formData.notes?.trim()) {
      newErrors.notes = 'Notes/Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Pre-compute the category list shown in the dropdown. For internal
  // tickets we narrow to the categories Naomi listed as relevant.
  const visibleCategories = formData.is_internal
    ? categories.filter((c) =>
        INTERNAL_CATEGORY_KEYWORDS.some((kw) =>
          (c.name || '').toLowerCase().includes(kw)
        )
      )
    : categories;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitLoading(true);
      
      const url = ticket ? createApiUrl(`api/tickets/${ticket.id}`) : createApiUrl('api/tickets');
      const method = ticket ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          ...formData,
          created_by: 'admin' // You would get this from auth context
        }),
      });

      const data = await response.json();

      if (response.ok) {
        onSubmit(data.ticket);
      } else {
        setErrors({ submit: data.error || 'Failed to save ticket' });
      }
    } catch (error) {
      setErrors({ submit: 'Network error occurred' });
    } finally {
      setSubmitLoading(false);
    }
  };

  const getCustomerDisplayName = (customer) => {
    const name = `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
    return customer.company_name 
      ? `${name} (${customer.company_name})`
      : name || customer.email_address || `Customer #${customer.id}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      {errors.submit && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errors.submit}
        </Alert>
      )}

      {/* TKT-185: Ticket Type toggle (Client vs Internal/Staff). Choosing
          Internal hides the customer section and limits the category
          dropdown to Equipment Issues / Complaint. */}
      <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          Ticket Type
        </Typography>
        <FormControl fullWidth sx={{ maxWidth: 360 }}>
          <InputLabel id="ticket-source-label">Who is this ticket for?</InputLabel>
          <Select
            labelId="ticket-source-label"
            value={formData.is_internal ? 'internal' : 'client'}
            label="Who is this ticket for?"
            onChange={(e) => {
              const internal = e.target.value === 'internal';
              setFormData((prev) => ({
                ...prev,
                is_internal: internal,
                // Clearing the customer when switching to Internal avoids
                // saving a stale customer reference on an internal ticket.
                customer_id: internal ? '' : prev.customer_id,
                // Clear the category too if the previously selected one
                // is filtered out for internal tickets.
                category_id: internal
                  ? (INTERNAL_CATEGORY_KEYWORDS.some((kw) =>
                      (categories.find((c) => c.id === prev.category_id)?.name || '')
                        .toLowerCase()
                        .includes(kw))
                      ? prev.category_id
                      : '')
                  : prev.category_id,
              }));
              if (errors.customer_id || errors.category_id) {
                setErrors((prev) => {
                  const next = { ...prev };
                  delete next.customer_id;
                  delete next.category_id;
                  return next;
                });
              }
            }}
          >
            <MenuItem value="client">Client-facing (a specific customer)</MenuItem>
            <MenuItem value="internal">Internal / Staff (equipment, internal complaint, no customer)</MenuItem>
          </Select>
          <FormHelperText>
            {formData.is_internal
              ? 'Internal tickets do not require a customer record. Categories are limited to Equipment Issues and Complaint.'
              : 'Pick a customer below so the ticket links to the right account.'}
          </FormHelperText>
        </FormControl>
      </Box>

      {/* Customer Selection Section - hidden for internal tickets */}
      {!formData.is_internal && (
      <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          Customer Information
        </Typography>
        
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              value={customers.find(c => c.id === formData.customer_id) || null}
              onChange={(event, newValue) => {
                handleInputChange('customer_id', newValue ? newValue.id : '');
              }}
              options={customers}
              getOptionLabel={getCustomerDisplayName}
              filterOptions={(options, { inputValue }) => {
                if (!inputValue) return options.slice(0, 50); // Limit initial display
                
                const filtered = options.filter(option => {
                  const searchText = inputValue.toLowerCase();
                  const name = `${option.first_name || ''} ${option.last_name || ''}`.toLowerCase();
                  const company = (option.company_name || '').toLowerCase();
                  const email = (option.email_address || '').toLowerCase();
                  const phone = (option.phone_number || '').toLowerCase();
                  
                  return name.includes(searchText) || 
                         company.includes(searchText) || 
                         email.includes(searchText) || 
                         phone.includes(searchText);
                });
                
                return filtered.slice(0, 100); // Limit results for performance
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Customers *"
                  error={!!errors.customer_id}
                  helperText={errors.customer_id || "Type to search by name, company, email, or phone"}
                  fullWidth
                  placeholder="Start typing customer name, company, email or phone..."
                  sx={{ 
                    minWidth: 400,
                    '& .MuiInputBase-root': {
                      minHeight: 56
                    }
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {getCustomerDisplayName(option)}
                      </Typography>
                      <Typography variant="caption" color="primary" fontWeight="medium">
                        ID: {option.id}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {option.email_address && (
                        <Typography variant="caption" color="text.secondary">
                          📧 {option.email_address}
                        </Typography>
                      )}
                      {option.phone_number && (
                        <Typography variant="caption" color="text.secondary">
                          📞 {option.phone_number}
                        </Typography>
                      )}
                      {option.city && (
                        <Typography variant="caption" color="text.secondary">
                          📍 {option.city}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </li>
              )}
              ListboxProps={{
                style: { maxHeight: 300 }
              }}
              noOptionsText="No customers found - try a different search term"
              loadingText="Loading customers..."
              clearOnBlur={false}
              selectOnFocus
              handleHomeEndKeys
            />
          </Grid>
        </Grid>
      </Box>
      )}

      {/* Ticket Details Section */}
      <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          Ticket Details
        </Typography>
        
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 8 }}>
            <FormControl fullWidth error={!!errors.category_id} sx={{ minWidth: 200 }}>
              <InputLabel>Category *</InputLabel>
              <Select
                value={formData.category_id}
                label="Category *"
                onChange={(e) => handleInputChange('category_id', e.target.value)}
              >
                {visibleCategories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.category_id && <FormHelperText>{errors.category_id}</FormHelperText>}
            </FormControl>
          </Grid>

          {/* TKT-186: removed Duration (hours) field. Tickets stay open
              until staff explicitly mark them complete; auto-expiry was
              not part of Paramount's intended workflow. */}

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Ticket Subject *"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              placeholder="A subject for the ticket"
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Notes/Description *"
              multiline
              rows={4}
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              error={!!errors.notes}
              helperText={errors.notes}
              placeholder="Detailed description of the issue, requirements, or customer request"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Task Assignment Section */}
      <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          Task Assignment (Optional)
        </Typography>
        
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth sx={{ minWidth: 180 }}>
              <InputLabel>Task Type</InputLabel>
              <Select
                value={formData.task_type_id}
                label="Task Type"
                onChange={(e) => handleInputChange('task_type_id', e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {taskTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth sx={{ minWidth: 150 }}>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority_id}
                label="Priority"
                onChange={(e) => handleInputChange('priority_id', e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {priorities.map((priority) => (
                  <MenuItem key={priority.id} value={priority.id}>
                    {priority.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Autocomplete
              value={users.find(u => u.id === formData.assigned_to_user_id) || null}
              onChange={(event, newValue) => {
                handleInputChange('assigned_to_user_id', newValue ? newValue.id : '');
              }}
              options={users}
              getOptionLabel={(user) => {
                if (!user) return '';
                const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
                return `${name} (${user.username}) - ${user.email || 'No email'}`;
              }}
              filterOptions={(options, { inputValue }) => {
                if (!inputValue) return options.slice(0, 20); // Show first 20 when no search
                
                const filtered = options.filter(option => {
                  const searchText = inputValue.toLowerCase();
                  const name = `${option.first_name || ''} ${option.last_name || ''}`.toLowerCase();
                  const username = (option.username || '').toLowerCase();
                  const email = (option.email || '').toLowerCase();
                  const userType = (option.user_type || '').toLowerCase();
                  
                  return name.includes(searchText) || 
                         username.includes(searchText) || 
                         email.includes(searchText) ||
                         userType.includes(searchText);
                });
                
                return filtered.slice(0, 50); // Limit results for performance
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assign To"
                  fullWidth
                  placeholder="Search by name, username, or email..."
                  helperText="Type to search for users by name, username, email, or user type"
                  sx={{ 
                    minWidth: 250,
                    '& .MuiInputBase-root': {
                      minHeight: 56
                    }
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {`${option.first_name || ''} ${option.last_name || ''}`.trim() || 'No Name'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography variant="caption" sx={{ 
                          bgcolor: option.user_type === 'admin' ? 'error.light' : 
                                  option.user_type === 'superadmin' ? 'warning.light' : 'primary.light',
                          color: 'white',
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          fontSize: '0.7rem'
                        }}>
                          {option.user_type || 'user'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary">
                        👤 @{option.username}
                      </Typography>
                      {option.email && (
                        <Typography variant="caption" color="text.secondary">
                          📧 {option.email}
                        </Typography>
                      )}
                      {option.employee_name && (
                        <Typography variant="caption" color="text.secondary">
                          👷 {option.employee_name}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </li>
              )}
              ListboxProps={{
                style: { maxHeight: 300 }
              }}
              noOptionsText="No users found - try a different search term"
              loadingText="Loading users..."
              clearOnBlur={false}
              selectOnFocus
              handleHomeEndKeys
              isClearable
            />
          </Grid>
        </Grid>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<CancelIcon />}
          onClick={onCancel}
          disabled={submitLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={submitLoading}
        >
          {submitLoading ? <CircularProgress size={20} /> : (ticket ? 'Update Ticket' : 'Create Ticket')}
        </Button>
      </Box>
    </Box>
  );
};

export default TicketForm; 